import React, { useState, useRef, useEffect } from 'react'
import { Switch, Popconfirm } from 'antd'
import classnames from 'classnames'
import {
  setEditorWatermark,
  setEditorActiveItem,
  setExpandedPanels
} from '../../../../actions/watermark'
import { useAppSelector, useAppDispatch } from '../../../../app/hooks'

import {
  ITEM_IDS,
  generateCustomItem,
  WATERMARK_CONFIG
} from '../../../../constants/watermark'
import {
  CloseCircleOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  RightOutlined
} from '@ant-design/icons'

interface ControllerPanelProps {
  onTabChange?: (key: string) => void
  clickEvent: (item: string, itemId?: string | number) => void
}

function ControllerPanel({ onTabChange, clickEvent }: ControllerPanelProps) {
  const [addItemLoading, setAddItemLoading] = useState(false)
  const customItemTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 从 Redux 获取状态
  const watermark = useAppSelector((state) => state.editor.watermark)
  const activeItem = useAppSelector((state) => state.editor.activeItem)
  const expandedPanels = useAppSelector((state) => state.editor.expandedPanels)
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      if (customItemTimerRef.current) clearTimeout(customItemTimerRef.current)
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current)
    }
  }, [])

  const handleActiveItemChange = (itemId: string | number | undefined) => {
    dispatch(setEditorActiveItem(itemId))
    // 使用数组操作添加展开的面板
    const newExpandedPanels = expandedPanels.includes(String(itemId))
      ? expandedPanels
      : [...expandedPanels, String(itemId)]
    dispatch(setExpandedPanels(newExpandedPanels))
    // 滚动到对应条目配置区。改用 scrollIntoView 而非 `window.location.href = '#id'`：
    // 后者会改写 URL hash 触发 hashchange，abtest SDK(abtest.esm.min.js) 监听 hashchange 会
    // 重新初始化、连带编辑器整树重挂载，CLEAR_EDITOR 清空 editor.watermark → 刚开启的开关被重置
    // （表现为开关一开即回弹、开启新条目把已开启条目重置）。scrollIntoView 不动 hash，等价滚动效果。
    scrollTimerRef.current = setTimeout(() => {
      document.getElementById(String(itemId))?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    }, 100)
  }

  const handleItemChange = (
    itemIndex: number,
    checked: boolean,
    e: React.MouseEvent
  ) => {
    e.stopPropagation()
    const newItems = watermark.items.map((item: any, idx: number) =>
      idx === itemIndex ? { ...item, switchStatus: checked } : item
    )
    dispatch(
      setEditorWatermark({
        ...watermark,
        items: newItems,
        update_time: String(new Date().getTime())
      })
    )
    if (checked && onTabChange) {
      onTabChange('content')
    }
  }

  const handleRemoveCustomItem = (
    itemId: string | number,
    e: React.MouseEvent | undefined
  ) => {
    e?.stopPropagation()
    const newItems = watermark.items.filter((item: any) => item.id !== itemId)
    dispatch(
      setEditorWatermark({
        ...watermark,
        items: newItems,
        update_time: String(new Date().getTime())
      })
    )
    clickEvent('closeCustomItem')
  }

  const handleAddCustomItem = () => {
    if (!addItemLoading) {
      const newItems = [...watermark.items, generateCustomItem()]
      dispatch(
        setEditorWatermark({
          ...watermark,
          items: newItems,
          update_time: String(new Date().getTime())
        })
      )
      setAddItemLoading(true)
      if (customItemTimerRef.current) clearTimeout(customItemTimerRef.current)
      customItemTimerRef.current = setTimeout(() => {
        setAddItemLoading(false)
      }, 1000)
      clickEvent('addCustomItem')
    }
  }

  const renderTheme = () => {
    /**
     * [v283]数据结构未对齐，落后于APP，部分水印未支持theme，做一下兼容
     */
    if (
      watermark.theme &&
      WATERMARK_CONFIG[watermark.base_id] &&
      WATERMARK_CONFIG[watermark.base_id].theme
    ) {
      return (
        <div
          className={classnames('controller-item', {
            active: activeItem === watermark.theme?.id
          })}
          onClick={() => {
            handleActiveItemChange(watermark.theme?.id)
            clickEvent('itemName', watermark.theme?.id)
          }}
        >
          <span className="display-name">
            {(WATERMARK_CONFIG[watermark.base_id].theme as any)?.displayName}
          </span>
          <RightOutlined />
        </div>
      )
    }
    return null
  }

  const renderLogoItem = () => {
    /**
     * [v283]数据结构未对齐，落后于APP，部分水印未支持logo，做一下兼容
     */
    if (watermark.logo && WATERMARK_CONFIG[watermark.base_id]?.logo) {
      return (
        <div
          className={classnames('controller-item', {
            active: activeItem === watermark.logo?.id
          })}
          onClick={() => {
            handleActiveItemChange(watermark.logo?.id)
            clickEvent('itemName', watermark.logo?.id)
          }}
        >
          <span className="display-name">
            {(WATERMARK_CONFIG[watermark.base_id].logo as any)?.displayName}
          </span>
          <Switch
            size="small"
            checked={watermark.logo?.switchStatus}
            onChange={(checked, e) => {
              e.stopPropagation()
              dispatch(
                setEditorWatermark({
                  ...watermark,
                  logo: {
                    ...watermark.logo,
                    switchStatus: checked
                  },
                  update_time: String(new Date().getTime())
                })
              )
              if (checked) {
                handleActiveItemChange(watermark.logo?.id)
                clickEvent('openSwitch', watermark.logo?.id)
              } else {
                clickEvent('closeSwitch', watermark.logo?.id)
              }
            }}
          />
        </div>
      )
    }
  }

  const renderItems = () => {
    // 字段列表
    const controllers = watermark.items
      .filter((item: any) => ITEM_IDS.includes(item.id))
      .map((item: any, index: number) => {
        const itemConfig = WATERMARK_CONFIG[watermark.base_id]?.[item.id] as any
        return (
          <div
            className={classnames('controller-item', {
              active: activeItem === item.id
            })}
            onClick={() => {
              handleActiveItemChange(item.id)
              clickEvent('itemName', item.id)
            }}
            key={index}
          >
            <span className="display-name">{item.title}</span>
            <Switch
              disabled={itemConfig && itemConfig.switchDisabled}
              size="small"
              checked={item.switchStatus}
              onChange={(checked, e) => {
                // 找到实际的 index
                const realIndex = watermark.items.findIndex(
                  (i: any) => i.id === item.id
                )
                handleItemChange(
                  realIndex,
                  checked,
                  e as unknown as React.MouseEvent
                )
                if (checked) {
                  handleActiveItemChange(item.id)
                  clickEvent('openSwitch', item.id)
                } else {
                  clickEvent('closeSwitch', item.id)
                }
              }}
            />
          </div>
        )
      })
    return controllers
  }

  const renderCustomItem = () => {
    // 自定义字段
    const controllers = watermark.items
      .filter((item: any) => !ITEM_IDS.includes(item.id))
      .map((item: any, index: number) => {
        return (
          <div
            className={classnames('controller-item', {
              active: activeItem === item.id
            })}
            onClick={() => handleActiveItemChange(item.id)}
            key={index}
          >
            <span className="display-name">{item.title}</span>
            <Popconfirm
              title="确定删除此项？"
              onConfirm={(e) =>
                handleRemoveCustomItem(
                  item.id,
                  e as unknown as React.MouseEvent
                )
              }
            >
              <CloseCircleOutlined />
            </Popconfirm>
          </div>
        )
      })
    return controllers
  }

  const renderCustomButton = () => {
    // 支持自定义项的水印，展示添加自定义项按钮
    if (WATERMARK_CONFIG[watermark.base_id]?.allowCustomItem) {
      if (addItemLoading) {
        return (
          <div className="add-item">
            <span>
              <LoadingOutlined />
              自定义项
            </span>
          </div>
        )
      }
      return (
        <div className="add-item" onClick={handleAddCustomItem}>
          <span>
            <PlusCircleOutlined />
            自定义项
          </span>
        </div>
      )
    }
    return null
  }

  if (watermark) {
    return (
      <div className="controller-panel">
        {/* {renderTheme()} */}
        {renderLogoItem()}
        {renderItems()}
        {renderCustomItem()}
        {renderCustomButton()}
      </div>
    )
  }
  return <div className="controller-panel"></div>
}

export default ControllerPanel
